import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { 
  Plus, Search, Eye, Edit, Trash2, Star, StarOff, 
  ArrowLeft, CheckCircle, Lock, Code, Palette,
  FileText, Copy, Download, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import api from '../../utils/api';

// Super Admin API helper
const adminApi = {
  get: (url) => api.get(url, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
  post: (url, data) => api.post(url, data, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
  put: (url, data) => api.put(url, data, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
  delete: (url) => api.delete(url, { headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` } }),
};

// Current plans from the plans table (slug + name); templates store plan slugs, empty = all plans
const usePlanOptions = () => {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    adminApi.get('/admin/plans').then((r) => setPlans(r.data || [])).catch(() => setPlans([]));
  }, []);
  return plans;
};

// ==================== Template List ====================
const TemplateList = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const planOptions = usePlanOptions();

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/templates/admin/list?include_inactive=true');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSetDefault = async (templateId) => {
    try {
      await adminApi.post(`/templates/admin/${templateId}/set-default`);
      toast.success('Default template updated');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set default');
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;
    try {
      await adminApi.delete(`/templates/admin/${templateToDelete}`);
      toast.success('Template deleted');
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const filteredTemplates = templates.filter(t => 
    t.template_name.toLowerCase().includes(search.toLowerCase()) ||
    t.template_slug.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="template-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quotation Templates</h1>
          <p className="text-gray-500">Manage HTML templates for quotation outputs (PDF, Email, Link)</p>
        </div>
        <Button onClick={() => navigate('/admin/templates/new')} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="template-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <div className="h-48 bg-gray-200 rounded-t-lg"></div>
              <CardContent className="pt-4">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-100 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <Card 
              key={template.template_id} 
              className={`overflow-hidden group hover:shadow-lg transition-shadow ${!template.is_active ? 'opacity-60' : ''}`}
              data-testid={`template-card-${template.template_slug}`}
            >
              {/* Thumbnail */}
              <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                {template.thumbnail_url ? (
                  <img 
                    src={template.thumbnail_url} 
                    alt={template.template_name}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileText className="w-16 h-16 text-gray-300" />
                  </div>
                )}
                
                {/* Overlay Actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button 
                    size="sm" 
                    variant="secondary"
                    onClick={() => navigate(`/admin/templates/${template.template_id}`)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="secondary"
                    onClick={() => navigate(`/admin/templates/${template.template_id}/preview`)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Preview
                  </Button>
                </div>

                {/* Badges */}
                <div className="absolute top-2 left-2 flex gap-2">
                  {template.is_default && (
                    <Badge className="bg-amber-500 text-white">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      Default
                    </Badge>
                  )}
                  {!template.is_active && (
                    <Badge variant="destructive">Inactive</Badge>
                  )}
                </div>
                <div className="absolute top-2 right-2">
                  <Badge variant="outline" className="bg-white/90">v{template.version}</Badge>
                </div>
              </div>

              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">{template.template_name}</h3>
                    <p className="text-sm text-gray-500">/{template.template_slug}</p>
                  </div>
                </div>

                {template.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{template.description}</p>
                )}

                {/* Allowed Plans */}
                <div className="flex flex-wrap gap-1 mb-4" data-testid={`template-plans-${template.template_id}`}>
                  {(() => {
                    const limited = planOptions.filter(p => (template.allowed_plans || []).includes(p.slug));
                    return limited.length === 0 ? (
                      <Badge variant="default" className="text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />All plans
                      </Badge>
                    ) : limited.map(p => (
                      <Badge key={p.slug} variant="default" className="text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />{p.name}
                      </Badge>
                    ));
                  })()}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t">
                  {!template.is_default && template.is_active && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleSetDefault(template.template_id)}
                      className="text-amber-600 hover:text-amber-700"
                    >
                      <Star className="w-4 h-4 mr-1" />
                      Set Default
                    </Button>
                  )}
                  <div className="flex-1"></div>
                  {!template.is_default && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setTemplateToDelete(template.template_id);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Empty State */}
          {filteredTemplates.length === 0 && (
            <div className="col-span-full text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
              <p className="text-gray-500 mb-4">
                {search ? 'Try a different search term' : 'Create your first quotation template'}
              </p>
              {!search && (
                <Button onClick={() => navigate('/admin/templates/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the template. Existing quotations using this template will continue to work.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== Template Editor ====================
const TemplateEditor = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
  // isNew is true if route is /new (templateId undefined) or explicitly 'new'
  const isNew = !templateId || templateId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState({
    template_name: '',
    template_slug: '',
    description: '',
    html_content: getDefaultTemplate(),
    css_content: '',
    allowed_plans: [],
    is_default: false,
    is_active: true
  });
  const planOptions = usePlanOptions();
  const [activeTab, setActiveTab] = useState('html');
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchTemplate();
    }
  }, [templateId, isNew]);

  const fetchTemplate = async () => {
    try {
      const response = await adminApi.get(`/templates/admin/${templateId}`);
      setTemplate(response.data);
    } catch (error) {
      toast.error('Failed to fetch template');
      navigate('/admin/templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template.template_name || !template.html_content) {
      toast.error('Template name and HTML content are required');
      return;
    }

    setSaving(true);
    try {
      // Drop stale/legacy plan names; only current plan slugs are stored
      const payload = {
        ...template,
        allowed_plans: (template.allowed_plans || []).filter(s => planOptions.some(p => p.slug === s)),
      };
      if (isNew) {
        await adminApi.post('/templates/admin/create', payload);
        toast.success('Template created successfully');
      } else {
        await adminApi.put(`/templates/admin/${templateId}`, payload);
        toast.success('Template updated successfully');
      }
      navigate('/admin/templates');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    // For preview, we'd ideally call a backend endpoint
    // For now, just show the raw HTML
    setPreviewHtml(template.html_content);
    setShowPreview(true);
  };

  const handlePlanToggle = (plan) => {
    const current = template.allowed_plans || [];
    if (current.includes(plan)) {
      setTemplate({ ...template, allowed_plans: current.filter(p => p !== plan) });
    } else {
      setTemplate({ ...template, allowed_plans: [...current, plan] });
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="template-editor">
      {/* Header */}
      <div className="p-4 border-b bg-white flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/templates')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-lg font-semibold">
              {isNew ? 'Create Template' : `Edit: ${template.template_name}`}
            </h1>
            {!isNew && <p className="text-sm text-gray-500">Version {template.version}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePreview}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            {isNew ? 'Create Template' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Settings */}
        <div className="w-80 border-r bg-gray-50 p-4 overflow-y-auto">
          <div className="space-y-4">
            <div>
              <Label htmlFor="template_name">Template Name *</Label>
              <Input
                id="template_name"
                value={template.template_name}
                onChange={(e) => {
                  const name = e.target.value;
                  setTemplate({ 
                    ...template, 
                    template_name: name,
                    template_slug: isNew ? generateSlug(name) : template.template_slug
                  });
                }}
                placeholder="e.g., Classic Blue"
                data-testid="template-name-input"
              />
            </div>

            <div>
              <Label htmlFor="template_slug">Slug</Label>
              <Input
                id="template_slug"
                value={template.template_slug}
                onChange={(e) => setTemplate({ ...template, template_slug: e.target.value })}
                placeholder="e.g., classic-blue"
                disabled={!isNew}
                data-testid="template-slug-input"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={template.description || ''}
                onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                placeholder="Brief description of this template..."
                rows={3}
              />
            </div>

            <div>
              <Label className="mb-3 block">Allowed Plans</Label>
              <div className="space-y-2">
                {planOptions.map(p => (
                  <div key={p.slug} className="flex items-center gap-2">
                    <Checkbox
                      id={`plan-${p.slug}`}
                      checked={(template.allowed_plans || []).includes(p.slug)}
                      onCheckedChange={() => handlePlanToggle(p.slug)}
                      data-testid={`template-plan-checkbox-${p.slug}`}
                    />
                    <label htmlFor={`plan-${p.slug}`} className="text-sm cursor-pointer">
                      {p.name}{p.is_public === false ? ' (hidden)' : ''}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Leave all unchecked to offer this template on every plan. If any are checked, only those plans see it.
              </p>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                <Label>Set as Default</Label>
                <p className="text-xs text-gray-500">Auto-selected for new quotations</p>
              </div>
              <Switch
                checked={template.is_default}
                onCheckedChange={(checked) => setTemplate({ ...template, is_default: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-gray-500">Available for use</p>
              </div>
              <Switch
                checked={template.is_active}
                onCheckedChange={(checked) => setTemplate({ ...template, is_active: checked })}
              />
            </div>
          </div>

          {/* Placeholder Reference */}
          <div className="mt-6 pt-4 border-t">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Available Placeholders</h3>
            <div className="text-xs text-gray-600 space-y-1 font-mono bg-white p-3 rounded border max-h-60 overflow-y-auto">
              <p className="font-semibold text-gray-800">Header:</p>
              <p>{'{{ header.logo_url }}'}</p>
              <p>{'{{ header.agency_name }}'}</p>
              <p>{'{{ header.package_title }}'}</p>
              <p>{'{{ header.route_summary }}'}</p>
              <p>{'{{ header.quote_reference }}'}</p>
              
              <p className="font-semibold text-gray-800 mt-2">Guest:</p>
              <p>{'{{ guest.guest_name }}'}</p>
              <p>{'{{ guest.guest_phone }}'}</p>
              <p>{'{{ guest.guest_email }}'}</p>
              <p>{'{{ guest.tour_start_date }}'}</p>
              <p>{'{{ guest.tour_end_date }}'}</p>
              
              <p className="font-semibold text-gray-800 mt-2">Passengers:</p>
              <p>{'{{ passengers.passenger_summary }}'}</p>
              
              <p className="font-semibold text-gray-800 mt-2">Rooms:</p>
              <p>{'{{ rooms.room_summary }}'}</p>
              
              <p className="font-semibold text-gray-800 mt-2">Route:</p>
              <p>{'{% for stop in route_visual %}'}</p>
              <p>{'  {{ stop.name }}'}</p>
              <p>{'  {{ stop.nights }}'}</p>
              <p>{'{% endfor %}'}</p>
              
              <p className="font-semibold text-gray-800 mt-2">Itinerary:</p>
              <p>{'{% for day in detailed_itinerary %}'}</p>
              <p>{'  {{ day.day_number }}'}</p>
              <p>{'  {{ day.day_title }}'}</p>
              <p>{'  {{ day.description }}'}</p>
              <p>{'  {{ day.overnight_at }}'}</p>
              <p>{'{% endfor %}'}</p>
              
              <p className="font-semibold text-gray-800 mt-2">Hotels:</p>
              <p>{'{% for cat, hotels in hotels_by_category.items() %}'}</p>
              <p>{'  {% for h in hotels %}'}</p>
              <p>{'    {{ h.hotel_name }}'}</p>
              <p>{'    {{ h.place }}'}</p>
              <p>{'    {{ h.room_type }}'}</p>
              <p>{'    {{ h.nights }}'}</p>
              <p>{'  {% endfor %}'}</p>
              <p>{'{% endfor %}'}</p>
              
              <p className="font-semibold text-gray-800 mt-2">Pricing:</p>
              <p>{'{% for row in pricing.pricing_rows %}'}</p>
              <p>{'  {{ row.category }}'}</p>
              <p>{'  {{ row.total_price }}'}</p>
              <p>{'  {{ row.per_person }}'}</p>
              <p>{'{% endfor %}'}</p>
              
              <p className="font-semibold text-gray-800 mt-2">Terms:</p>
              <p>{'{{ terms.inclusions }}'}</p>
              <p>{'{{ terms.exclusions }}'}</p>
              <p>{'{{ terms.payment_terms }}'}</p>
              <p>{'{{ terms.cancellation_policy }}'}</p>
              
              <p className="font-semibold text-gray-800 mt-2">Payment:</p>
              <p>{'{{ payment.bank_name }}'}</p>
              <p>{'{{ payment.account_number }}'}</p>
              <p>{'{{ payment.upi_id }}'}</p>
              <p>{'{{ payment.qr_code_url }}'}</p>
            </div>
          </div>
        </div>

        {/* Right Panel - Code Editor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <div className="border-b px-4">
              <TabsList className="h-12">
                <TabsTrigger value="html" className="gap-2">
                  <Code className="w-4 h-4" />
                  HTML
                </TabsTrigger>
                <TabsTrigger value="css" className="gap-2">
                  <Palette className="w-4 h-4" />
                  Custom CSS
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="html" className="flex-1 m-0 overflow-hidden">
              <textarea
                value={template.html_content}
                onChange={(e) => setTemplate({ ...template, html_content: e.target.value })}
                className="w-full h-full p-4 font-mono text-sm resize-none focus:outline-none border-0"
                placeholder="Enter HTML template with Jinja2 placeholders..."
                spellCheck={false}
                data-testid="html-editor"
              />
            </TabsContent>

            <TabsContent value="css" className="flex-1 m-0 overflow-hidden">
              <textarea
                value={template.css_content || ''}
                onChange={(e) => setTemplate({ ...template, css_content: e.target.value })}
                className="w-full h-full p-4 font-mono text-sm resize-none focus:outline-none border-0"
                placeholder="/* Custom CSS styles */&#10;.custom-class {&#10;  color: #1e40af;&#10;}"
                spellCheck={false}
                data-testid="css-editor"
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>
              This is a raw HTML preview. Actual output will have data injected.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto border rounded-lg bg-white">
            <iframe
              srcDoc={previewHtml}
              className="w-full h-[600px]"
              title="Template Preview"
              sandbox="allow-same-origin"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== Default Template HTML ====================
function getDefaultTemplate() {
  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>{{ header.package_title }}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', sans-serif; line-height: 1.6; color: #1f2937; }
        .container { max-width: 800px; margin: 0 auto; padding: 20px; }
        .header { text-align: center; padding: 30px; background: #1e40af; color: white; }
        .header h1 { font-size: 24px; margin-bottom: 8px; }
        .section { padding: 20px; border-bottom: 1px solid #e5e7eb; }
        .section-title { font-size: 14px; font-weight: 600; color: #1e40af; margin-bottom: 15px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 10px; text-align: left; border-bottom: 1px solid #e5e7eb; }
        th { background: #f8fafc; font-size: 12px; color: #6b7280; text-transform: uppercase; }
        {{ custom_css }}
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            {% if header.logo_url %}
            <img src="{{ header.logo_url }}" alt="{{ header.agency_name }}" style="max-height: 50px; margin-bottom: 15px;">
            {% endif %}
            <h1>{{ header.package_title }}</h1>
            <p>{{ header.route_summary }}</p>
            <p style="margin-top: 10px; font-size: 12px;">Ref: {{ header.quote_reference }}</p>
        </div>
        
        <div class="section">
            <div class="section-title">Guest Details</div>
            <p><strong>Name:</strong> {{ guest.guest_name }}</p>
            <p><strong>Contact:</strong> {{ guest.guest_phone }}</p>
            <p><strong>Travel Dates:</strong> {{ guest.tour_start_date }} - {{ guest.tour_end_date }}</p>
            <p><strong>Travelers:</strong> {{ passengers.passenger_summary }}</p>
        </div>
        
        <div class="section">
            <div class="section-title">Itinerary</div>
            {% for day in detailed_itinerary %}
            <div style="margin-bottom: 15px; padding: 10px; background: #f8fafc; border-radius: 8px;">
                <h4 style="color: #1e40af;">Day {{ day.day_number }}: {{ day.day_title }}</h4>
                <p style="font-size: 13px; color: #4b5563;">{{ day.description }}</p>
                {% if day.overnight_at %}
                <p style="font-size: 12px; color: #6b7280; margin-top: 8px;">Overnight: {{ day.overnight_at }}</p>
                {% endif %}
            </div>
            {% endfor %}
        </div>
        
        <div class="section">
            <div class="section-title">Pricing</div>
            <table>
                <thead>
                    <tr>
                        <th>Category</th>
                        <th style="text-align: right;">Total Price</th>
                        <th style="text-align: right;">Per Person</th>
                    </tr>
                </thead>
                <tbody>
                    {% for row in pricing.pricing_rows %}
                    <tr>
                        <td>{{ row.category }}</td>
                        <td style="text-align: right;">{{ row.total_price }}</td>
                        <td style="text-align: right;">{{ row.per_person }}</td>
                    </tr>
                    {% endfor %}
                </tbody>
            </table>
        </div>
        
        <div class="section">
            <div class="section-title">Terms & Conditions</div>
            {% if terms.inclusions %}
            <div style="margin-bottom: 15px;">
                <h5 style="font-size: 12px; color: #1e40af;">Inclusions</h5>
                <p style="font-size: 12px; white-space: pre-line;">{{ terms.inclusions }}</p>
            </div>
            {% endif %}
            {% if terms.exclusions %}
            <div style="margin-bottom: 15px;">
                <h5 style="font-size: 12px; color: #1e40af;">Exclusions</h5>
                <p style="font-size: 12px; white-space: pre-line;">{{ terms.exclusions }}</p>
            </div>
            {% endif %}
        </div>
        
        <div style="text-align: center; padding: 20px; background: #1e3a5f; color: white; font-size: 12px;">
            <p><strong>{{ header.agency_name }}</strong></p>
            <p>{{ header.agency_phone }} | {{ header.agency_email }}</p>
        </div>
    </div>
</body>
</html>`;
}

// ==================== Main Component ====================
const TemplateManagement = () => {
  return (
    <Routes>
      <Route path="/" element={<TemplateList />} />
      <Route path="/new" element={<TemplateEditor />} />
      <Route path="/:templateId" element={<TemplateEditor />} />
    </Routes>
  );
};

export default TemplateManagement;
